import { Brand, createBranded } from '@deltasierra/type-utilities';
import {
    FullAuditable,
    FullAuditableKeys,
    HasId,
    ObjectOrVoid,
    PathBuilder,
    Omit,
    ApiRoute,
    HttpMethod,
} from '../common';
import { BasicClientInfo, ClientId } from '../client';
import { LocationId } from '../location';
import { BuilderTemplateId } from '../builderTemplate';
import { StripeConfigurationId } from '../integration';
import {
    GetPrintProviderSKUGroupQuery,
    GetPrintProviderSKUGroupResponse,
    PrintProviderSKUGroupId,
} from './PrintProviderSKUGroup';
import { PrintProviderStats } from './PrintProviderStats';

export type PrintProviderId = Brand<number, 'PrintProviderId'>;
export const PrintProviderId = createBranded<PrintProviderId>();

export interface PrintProvider extends HasId<PrintProviderId>, FullAuditable {
    address: string | null;
    businessName: string;
    clientId: ClientId | null;
    email: string;
    isSingleUse: boolean;
    locationId: LocationId | null;
    phoneNumber: string | null;
    printProviderSKUGroupId: PrintProviderSKUGroupId | null;
    stripeConfigurationId: StripeConfigurationId | null;
}

export const PRINT_ORDER_NUMBER_PREFIX = 'DS-PRINT-';
const PRINT_PROVIDER_API_ROOT = <T extends ObjectOrVoid>(path: PathBuilder<T>) =>
    path.literal('print').literal('provider');
const PRINT_PROVIDER_CLIENT_API = <T extends ObjectOrVoid>(path: PathBuilder<T>) =>
    PRINT_PROVIDER_API_ROOT(path).literal('client');
const PRINT_PROVIDER_LOCATION_API = <T extends ObjectOrVoid>(path: PathBuilder<T>) =>
    PRINT_PROVIDER_API_ROOT(path).literal('location');

export interface ICreatePrintProviderBody {
    address: string | null;
    businessName: string;
    email: string;
    phoneNumber: string | null;
    printProviderSKUGroupId?: PrintProviderSKUGroupId | null;
    stripeConfigurationId?: StripeConfigurationId | null;
}

export interface SetPrintProviderSKUGroupBody {
    printProviderSKUGroupId: PrintProviderSKUGroupId | null;
}

interface LocationParams {
    id: LocationId;
}

interface ClientParams {
    id: ClientId;
}

export interface GetPrintOrderNumberResponse {
    printOrderNumber: string;
}

export interface GetClientExclusivePrintProviderQuery {
    builderTemplateId: BuilderTemplateId;
}

export interface GetClientExclusivePrintProviderResponse {
    printProvider: PrintProvider | null;
}

export type PrintProviderDto = Omit<PrintProvider, FullAuditableKeys>;

export interface PrintProviderDtoWithBasicClientInfo extends PrintProviderDto {
    clientId: ClientId;
    client: BasicClientInfo;
}

interface PrintProviderParams {
    id: PrintProviderId;
}

export const PrintProviderApi = {
    deletePrintProvider: new ApiRoute<void, PrintProviderParams, void, void>(HttpMethod.Delete, path =>
        PRINT_PROVIDER_API_ROOT(path).param('id'),
    ),
    createOnceOffPrintProvider: new ApiRoute<PrintProvider, void, ICreatePrintProviderBody, void>(
        HttpMethod.Post,
        PRINT_PROVIDER_API_ROOT,
    ),
    getPrintOrderNumber: new ApiRoute<GetPrintOrderNumberResponse, void, void, void>(HttpMethod.Get, path =>
        path.literal('print').literal('orderNumber'),
    ),
    getSkuGroup: new ApiRoute<
        GetPrintProviderSKUGroupResponse,
        PrintProviderParams,
        void,
        GetPrintProviderSKUGroupQuery
    >(HttpMethod.Get, path => path.literal('print').literal('provider').param('id').literal('skuGroup')),
    setPrintProviderSKUGroup: new ApiRoute<void, PrintProviderParams, SetPrintProviderSKUGroupBody, void>(
        HttpMethod.Patch,
        path => path.literal('print').literal('provider').param('id').literal('skuGroup'),
    ),
    location: {
        getPrintProvider: new ApiRoute<PrintProvider, LocationParams, void, void>(HttpMethod.Get, path =>
            PRINT_PROVIDER_LOCATION_API(path).param('id'),
        ),
        getAvailableProviders: new ApiRoute<PrintProvider[], LocationParams, void, void>(HttpMethod.Get, path =>
            PRINT_PROVIDER_LOCATION_API(path).param('id').literal('all'),
        ),
        createPrintProvider: new ApiRoute<PrintProvider, LocationParams, ICreatePrintProviderBody, void>(
            HttpMethod.Post,
            path => PRINT_PROVIDER_LOCATION_API(path).param('id'),
        ),
        getStats: new ApiRoute<PrintProviderStats[], LocationParams, void, void>(HttpMethod.Post, path =>
            PRINT_PROVIDER_LOCATION_API(path).param('id').literal('stats'),
        ),
    },
    client: {
        getPrintProvider: new ApiRoute<PrintProvider, ClientParams, void, void>(HttpMethod.Get, path =>
            PRINT_PROVIDER_CLIENT_API(path).param('id'),
        ),
        createPrintProvider: new ApiRoute<PrintProvider, ClientParams, ICreatePrintProviderBody, void>(
            HttpMethod.Post,
            path => PRINT_PROVIDER_CLIENT_API(path).param('id'),
        ),
        getExclusivePrintProvider: new ApiRoute<
            GetClientExclusivePrintProviderResponse,
            ClientParams,
            void,
            GetClientExclusivePrintProviderQuery
        >(HttpMethod.Get, path =>
            path.literal('print').literal('provider').literal('client').param('id').literal('exclusive'),
        ),
    },
};
